import currency from 'currency.js';
import { z } from 'zod';

import { CONVERSION_KPI_OPTION } from '../constants/lineItems';
import { addCommas, removeCommas } from '../util/helpers';

// ADOPS-2639: line-item bonus must be >= sum of profile bonuses when profiles
// exist. Exported so tests can call it directly (top-level .superRefine is
// skipped when any field-level check fails).
export const checkBonusFloor = (data: {
  bonus?: string | number | null;
  customDeliveryProfiles?: { bonus?: string | number | null | undefined }[] | null;
}): { message: string; profileBonusSum: number } | null => {
  const profiles = data.customDeliveryProfiles;
  if (!profiles || profiles.length === 0) return null;

  // Match the idiom used in LineItemTimingSection / CustomDeliveryProfileModal.
  const toNumber = (value: string | number | null | undefined) =>
    Number(removeCommas((value ?? 0) as string | number)) || 0;

  const profileBonusSum = profiles.reduce((sum, p) => sum.add(toNumber(p.bonus)), currency(0)).value;
  if (profileBonusSum === 0) return null;

  const lineItemBonus = toNumber(data.bonus);
  if (lineItemBonus < profileBonusSum) {
    return {
      message: `Bonus must be at least ${addCommas(profileBonusSum)} to cover the bonus impressions allocated across custom flighting profiles.`,
      profileBonusSum,
    };
  }
  return null;
};

const stringToNumberPreprocessor = (value) => {
  const processed = z.string().transform(Number).safeParse(value);
  return processed.success ? processed.data : value;
};

const coreKpiValueSchema = z.preprocess(
  stringToNumberPreprocessor,
  z
    .number({
      invalid_type_error: 'Required',
    })
    .positive()
    .or(z.number().negative())
);

// A single KPI objective row. Conversion is a scored-list-only Secondary KPI (hidden
// goal input) and is validated against its fixed sentinel value; every other KPI
// requires a real positive/negative goal.
export const kpiObjectiveSchema = z
  .object({
    name: z.string(),
    value: z.any(),
    type: z.string(),
    adGroupIds: z.array(z.number()).nullish(),
  })
  .superRefine((objective, ctx) => {
    if (objective.name === CONVERSION_KPI_OPTION.value) {
      const isSentinel =
        objective.value === CONVERSION_KPI_OPTION.defaultValue ||
        objective.value === Number(CONVERSION_KPI_OPTION.defaultValue);
      if (!isSentinel) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['value'],
          message: `Conversion goal must be ${CONVERSION_KPI_OPTION.defaultValue}`,
        });
      }
      return;
    }
    const result = coreKpiValueSchema.safeParse(objective.value);
    if (!result.success) {
      result.error.issues.forEach((issue) => ctx.addIssue({ ...issue, path: ['value', ...issue.path] }));
    }
  });

// ADOPS-2556: Receipts no longer requires both product AND category. The user must
// select at least one product OR at least one category. Exported so tests can call it
// directly (the .superRefine is skipped when a field-level check fails).
export const receiptProductListSchema = z
  .object({
    id: z.number().optional(),
    _destroy: z.boolean().optional(),
    // Default to [] so an omitted array doesn't fail with a generic "Required" and skip
    // the cross-field .superRefine below — the OR rule should always be the one to fire.
    productList: z.array(z.any()).default([]),
    productLowerCategories: z.array(z.any()).default([]),
  })
  .superRefine((val, ctx) => {
    // Skip the selection requirement when the record is being deleted (e.g. disabling an
    // existing Receipts KPI submits a destroy payload with empty selections).
    const { _destroy: markedForDestroy } = val;
    if (markedForDestroy) return;
    const hasProduct = (val.productList?.length ?? 0) > 0;
    const hasCategory = (val.productLowerCategories?.length ?? 0) > 0;
    if (!hasProduct && !hasCategory) {
      const message = 'Select at least one product or category';
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['productList'], message });
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['productLowerCategories'], message });
    }
  });

const SeedTypesSchema = z.record(z.string(), z.array(z.number()).nullish());

const CampaignsSchema = z.array(
  z.object({
    id: z.number(),
    seedTypeIds: z.array(z.number()),
  })
);

const LineItemSchema = z.object({
  lineItemId: z.number().nullable(),
  lineItemName: z.string().min(1, { message: 'Line item name is required' }),
  insertionOrderNumber: z.string().nullable(),
  contractId: z.number().nullable(),
  mediaType: z.string(),
  targetStrategy: z.string(),
  thinklabState: z.string(),
  startDate: z.date().nullable(),
  endDate: z.date().nullable(),
  frequencyCapEnabled: z.boolean(),
  frequencyCapping: z.string(),
  frequencyCappingScope: z.string(),
  maxUserViewsPerPeriod: z.string().or(z.number()),
  performanceCapEnabled: z.boolean(),
  clickFrequencyCapping: z.string(),
  clickFrequencyCappingScope: z.string(),
  maxUserClicksPerPeriod: z.string().or(z.number()),
  costIncurringFeatures: z.array(z.string()),
  advisoryBudget: z.string().or(z.number()),
  impressionBudget: z.string().or(z.number()),
  bonus: z.string().or(z.number()),
  pricingRate: z.string().or(z.number()),
  optimizationType: z.string(),
  minCtrOptimization: z.string(),
  minConversionOptimization: z.string(),
  bidPriority: z.number(),
  intradaySpendPacing: z.boolean(),
  bidFrequency: z.number(),
  dynamicSpendPacing: z.boolean(),
  pacingStrategies: z.array(z.string()),
  dailyImpressionLimit: z.string().or(z.number()),
  dollarBudget: z.string().or(z.number()),
  nonRevenueEnabled: z.boolean(),
  uiState: z.string(),
  deliveryStatus: z.string(),
  pidType: z.string(),
  optimizationTypeIds: z.array(z.number()),
  kpiObjectives: z.array(kpiObjectiveSchema),
  kpiGoal: z.object({
    coreKpi: z.array(z.record(z.string().min(1, 'Required'), z.string())).nullish(),
    secondaryKpi: z.array(z.record(z.string().min(1, 'Required'), z.string())).max(1),
  }),
  scoredListMetric: z.string(),
  visitStoreList: z
    .object({
      id: z.number().optional(),
      _destroy: z.boolean().optional(),
      storeList: z.array(z.any()).min(1, 'Required'),
    })
    .nullish(),
  conversionPixelUrl: z
    .object({
      id: z.number().optional(),
      _destroy: z.boolean().optional(),
      pixelUrl: z.string({
        invalid_type_error: 'Required',
      }),
    })
    .nullish(),
  receiptProductList: receiptProductListSchema.nullish(),
  seedTypeIds: SeedTypesSchema.nullish(),
  campaigns: CampaignsSchema,
  customDeliveryProfiles: z.array(
    z.object({
      id: z.number().or(z.string()).optional(),
      io_id: z.string().optional(),
      startDate: z.date().nullable().optional(),
      endDate: z.date().nullable().optional(),
      bonus: z.string().or(z.number()).optional(),
      dollarBudget: z.string().or(z.number()).optional(),
      deliveredImpressions: z.string().or(z.number()).optional(),
      disabled: z.boolean().optional(),
      active: z.boolean().optional(),
    }).passthrough()
  ).superRefine((profiles, ctx) => {
    if (!profiles || profiles.length === 0) return;

    const isEmptyValue = (val: string | number | null | undefined) =>
      val === null || val === undefined || val === '';

    profiles.forEach((profile, index) => {
      if (!profile.startDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Start Date is required',
          path: [index, 'requiredFieldError'],
        });
      }
      if (!profile.endDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'End Date is required',
          path: [index, 'requiredFieldError'],
        });
      }
      if (isEmptyValue(profile.bonus)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Bonus is required',
          path: [index, 'requiredFieldError'],
        });
      }
      if (isEmptyValue(profile.dollarBudget)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Dollar Budget is required',
          path: [index, 'requiredFieldError'],
        });
      }
    });

    if (profiles.length < 2) return;

    const dated = profiles
      .map((p, i) => ({ ...p, originalIndex: i }))
      .filter((p) => p.startDate && p.endDate);

    dated.sort((a, b) => a.startDate!.getTime() - b.startDate!.getTime());

    for (let i = 1; i < dated.length; i += 1) {
      const prev = dated[i - 1];
      const curr = dated[i];
      if (curr.startDate!.getTime() <= prev.endDate!.getTime()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Delivery profiles can't have overlapping flight dates",
          path: [prev.originalIndex, 'overlapError'],
        });
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Delivery profiles can't have overlapping flight dates",
          path: [curr.originalIndex, 'overlapError'],
        });
      }
    }
  }).nullish(),
  deletedProfileIds: z.array(z.number()).optional(),
}).superRefine((data, ctx) => {
  const violation = checkBonusFloor(data);
  if (violation) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: violation.message,
      path: ['bonus'],
    });
  }
});

export type LineItem = z.infer<typeof LineItemSchema>;

export type SeedTypes = z.infer<typeof SeedTypesSchema>;
export type Campaigns = z.infer<typeof CampaignsSchema>;

export default LineItemSchema;
